import React, { useMemo, useState } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { EiqButton, EiqInput, EiqSelect } from 'eiq-storybook';

import { useCreateJobStakeholderContact } from 'src/hooks/jobExternalStakeholder';
import { getAffiliationOptionsList } from 'src/components/JobForm/utils';
import Style from './SiteRepresentativeInvitePopup.styles';
import { siteRepresentativeInviteSchema } from './schema';

const SiteRepresentativeInvitePopup = ({
  jobId,
  jobExternalStakeholders: jobExternalStakeholdersReal,
  isLoadingJobExternalStakeholders,
  onClose,
  onSubmit,
  auth,
  job, 
  companyTypes,
  serviceType = 'cap'
  }) => {
  
  const { t } = useTranslation(); 
  const [formValid, setFormValid] = useState(false);
  const {
    isLoading: isCreatingJobStakeholderContact,
    createJobStakeholderContactCallback: createJobStakeholderContact,
  } = useCreateJobStakeholderContact();

  const isEmailDuplicate = (email, jobExternalStakeholders) => {
    if(jobExternalStakeholders?.length === 0) {
      return false;
    }
    const checkDuplicate = jobExternalStakeholders.some((ext) => ext.user.email.toLowerCase() === email.toLowerCase());
    return checkDuplicate;
  };

  const affiliationOptionsList = useMemo(() => {
    // remove below code and handle directly from getAffiliationOptionsList after CAP revamp
    const updatedAffiliationOptionsList = getAffiliationOptionsList(job, companyTypes).map(({label, value}) => ({
      value,
      label: label.includes('(Vendor)') ? 'Vendor' : 'Factory',
    }));
    const { isVendorUser, isFactoryUser, isCapManager, isSaqManager, isAuditor } = auth;
    if (isCapManager || isSaqManager || isAuditor || auth.isAdmin) {
      return updatedAffiliationOptionsList;
    }
    if (isVendorUser) {
      return updatedAffiliationOptionsList.filter(option => option.label === 'Vendor');
    }
    if (isFactoryUser) {
      return updatedAffiliationOptionsList.filter(option => option.label === 'Factory');
    }
    return [];
  }, [job]);

  const handleInviteSubmit = async (values = {}, setFieldError) => {
    const emailIsDuplicate = isEmailDuplicate(values.email, jobExternalStakeholdersReal);

    if(!emailIsDuplicate) {
      const response = await createJobStakeholderContact({
        jobId,
        companyId: values.role?.value,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        serviceType
      });

      if (response) {
        onSubmit();
        if (!isLoadingJobExternalStakeholders) onClose();
      }
    } else {
      setFieldError('email', 'DUPLICATE_EMAIL_SITE_REPRESENTSTIVE_INVITE');
    }
  }

  return (
    <Style.Container>
      <Style.ContainerInner>
        <Style.Title>{t('INVITE_SITE_RESPRESENTATIVE')}</Style.Title>
        <Formik validationSchema={siteRepresentativeInviteSchema} initialValues={{
            firstName: '',
            lastName: '',
            email: '',
            role: ''
          }}
        >
          {({ setFieldValue, values, setFieldError, errors, touched }) => {
            const hasErrors = Object.keys(errors).length > 0;
            const isTouched = Object.keys(touched).length > 0;
            const hasValues = Object.values(values).some((value) => value !== '');
        
            const isFormValid = !hasErrors && (isTouched || hasValues);

            setFormValid(isFormValid);
            return (
              <>
                <Style.RowInput>
                  <EiqInput 
                    type="text" 
                    label={t('COLUMN_FIRST_NAME')} 
                    name='firstName' 
                    wrapperClassName='InputWrapper'
                    errorMessage={t(errors.firstName)}
                    value={values.firstName}
                    onChange={(e) => {
                      const val = e.target.value;
                        setFieldValue('firstName', val)
                      }
                    }
                  />
                </Style.RowInput>
                <Style.RowInput>
                  <EiqInput 
                    type="text" 
                    label={t('COLUMN_LAST_NAME')} 
                    name='lastName' 
                    errorMessage={t(errors.lastName)}
                    wrapperClassName='InputWrapper'
                    value={values.lastName}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFieldValue('lastName', val)
                      }
                    }
                  />
                </Style.RowInput>
                <Style.RowInput>
                  <EiqInput 
                    type="email" 
                    label={t('EMAIL')}
                    errorMessage={t(errors.email)}
                    wrapperClassName='InputWrapper'
                    name='email' 
                    value={values.email}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFieldValue('email', val);
                    }}
                    />
                </Style.RowInput>
                <Style.RowInput>
                  <EiqSelect 
                    label={t('ROLE')}
                    name="role"
                    error={t(errors.role)}
                    placeholder={""}
                    hasChips={true}
                    touched={errors.role}
                    wrapperClass='SelectInputWrapper'
                    options={affiliationOptionsList}
                    onChange={(e, option) => setFieldValue('role', option)}
                    isClearable={false}
                    value={values?.role}
                  />
                </Style.RowInput>
                <Style.ButtonWrapper>
                  <EiqButton 
                    data-testid="cancelButton"
                    type="cancel" 
                    onClick={onClose}
                    variant='btn--outline-link'
                    label={t('CANCEL')}
                    disabled={isLoadingJobExternalStakeholders || isCreatingJobStakeholderContact}
                  />
                  <EiqButton 
                    data-testid="inviteButton"
                    variant='btn--outline-link'
                    onClick={() => {
                      handleInviteSubmit(values, setFieldError);
                    }}
                    label={t('INVITE')}
                    disabled={!formValid || 
                      isLoadingJobExternalStakeholders || 
                      isCreatingJobStakeholderContact
                    }
                  />
                </Style.ButtonWrapper>
              </>
            )
          }}
        </Formik>
      </Style.ContainerInner>
    </Style.Container>
  );
};

export default SiteRepresentativeInvitePopup;
